/**
 * CredentialDetailsTab Component
 * Renders type-specific form fields for credentials
 */

import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// Import specialized credential forms
import { FTPCredentialForm } from '@/components/credentials/FTPCredentialForm';
import { MsSqlCredentialForm } from '@/components/credentials/MsSqlCredentialForm';
import { MySqlCredentialForm } from '@/components/credentials/MySqlCredentialForm';
import { MongoDBCredentialForm } from '@/components/credentials/MongoDBCredentialForm';
import { PostgreSqlCredentialForm } from '@/components/credentials/PostgreSqlCredentialForm';
import { RedisCredentialForm } from '@/components/credentials/RedisCredentialForm';
import { ElasticsearchCredentialForm } from '@/components/credentials/ElasticsearchCredentialForm';
import { AzureBlobStorageCredentialForm } from '@/components/credentials/AzureBlobStorageCredentialForm';
import { AccessDocumentCredentialForm } from '@/components/credentials/AccessDocumentCredentialForm';
import { DonorfyCredentialForm } from '@/components/credentials/DonorfyCredentialForm';
import { PeopleHRCredentialForm } from '@/components/credentials/PeopleHRCredentialForm';
import { PayCircleCredentialForm } from '@/components/credentials/PayCircleCredentialForm';
import { AccessCharityCrmCredentialForm } from '@/components/credentials/AccessCharityCrmCredentialForm';
// Note: Microsoft OAuth forms are handled directly in CredentialDialog with flattened tabs

interface CredentialDetailsTabProps {
  /** Credential type */
  credentialType: string;

  /** Whether we're editing an existing credential */
  isEditing: boolean;

  /** Form data */
  formData: any;

  /** Callback to update form data */
  onChange: (updates: any) => void;

  /** Credential ID (for OAuth flows) */
  credentialId?: string;
}

/**
 * Details tab content with name and type-specific fields
 *
 * @example
 * <CredentialDetailsTab
 *   credentialType="sql_server"
 *   isEditing={false}
 *   formData={formData}
 *   onChange={(updates) => setFormData(prev => ({ ...prev, ...updates }))}
 * />
 */
export const CredentialDetailsTab: React.FC<CredentialDetailsTabProps> = ({
  credentialType,
  isEditing,
  formData,
  onChange,
  credentialId,
}) => {

  // Render type-specific form fields
  const renderTypeFields = () => {
    switch (credentialType) {
      // Simple API Key types
      case 'api_key':
      case 'claude':
      case 'openai':
      case 'data-engine':
        return (
          <div className="space-y-2">
            <Label htmlFor="value">API Key *</Label>
            <Input
              id="value"
              type="password"
              placeholder="Enter API key"
              value={formData.value || ''}
              onChange={(e) => onChange({ value: e.target.value })}
            />
            {credentialType === 'claude' && (
              <p className="text-xs text-muted-foreground">
                Get your API key from console.anthropic.com
              </p>
            )}
            {credentialType === 'openai' && (
              <p className="text-xs text-muted-foreground">
                Get your API key from platform.openai.com
              </p>
            )}
          </div>
        );

      case 'azure-openai':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="endpoint">Endpoint *</Label>
              <Input
                id="endpoint"
                placeholder="https://your-resource.openai.azure.com"
                value={formData.endpoint || ''}
                onChange={(e) => onChange({ endpoint: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="value">API Key *</Label>
              <Input
                id="value"
                type="password"
                placeholder="Enter Azure OpenAI API key"
                value={formData.value || ''}
                onChange={(e) => onChange({ value: e.target.value })}
              />
            </div>
          </div>
        );

      case 'bearer_token':
        return (
          <div className="space-y-2">
            <Label htmlFor="value">Token *</Label>
            <Input
              id="value"
              type="password"
              placeholder="Enter bearer token"
              value={formData.value || ''}
              onChange={(e) => onChange({ value: e.target.value })}
            />
          </div>
        );

      case 'basic_auth':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username *</Label>
              <Input
                id="username"
                placeholder="Enter username"
                value={formData.username || ''}
                onChange={(e) => onChange({ username: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter password"
                value={formData.password || ''}
                onChange={(e) => onChange({ password: e.target.value })}
              />
            </div>
          </div>
        );

      case 'api_key_header':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="headerName">Header Name *</Label>
              <Input
                id="headerName"
                placeholder="X-API-Key"
                value={formData.headerName || ''}
                onChange={(e) => onChange({ headerName: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="value">API Key *</Label>
              <Input
                id="value"
                type="password"
                placeholder="Enter API key"
                value={formData.value || ''}
                onChange={(e) => onChange({ value: e.target.value })}
              />
            </div>
          </div>
        );

      // Specialized forms using existing components
      case 'ftp':
        return (
          <FTPCredentialForm
            value={formData.ftpData || {}}
            onChange={(ftpData) => onChange({ ftpData })}
          />
        );

      case 'msSqlCredential':
        return (
          <MsSqlCredentialForm
            formData={formData.msSqlData || {}}
            onChange={(updates) =>
              onChange({ msSqlData: { ...formData.msSqlData, ...updates } })
            }
            isEditing={isEditing}
          />
        );

      case 'postgresql':
        return (
          <PostgreSqlCredentialForm
            formData={formData.postgreSqlData || {}}
            onChange={(updates) =>
              onChange({ postgreSqlData: { ...formData.postgreSqlData, ...updates } })
            }
            isEditing={isEditing}
          />
        );

      case 'mysql':
        return (
          <MySqlCredentialForm
            formData={formData.mySqlData || {}}
            onChange={(updates) =>
              onChange({ mySqlData: { ...formData.mySqlData, ...updates } })
            }
            isEditing={isEditing}
          />
        );

      case 'mongodb':
        return (
          <MongoDBCredentialForm
            formData={formData.mongoDBData || {}}
            onChange={(updates) =>
              onChange({ mongoDBData: { ...formData.mongoDBData, ...updates } })
            }
            isEditing={isEditing}
          />
        );

      case 'redis':
        return (
          <RedisCredentialForm
            formData={formData.redisData || {}}
            onChange={(updates) =>
              onChange({ redisData: { ...formData.redisData, ...updates } })
            }
            isEditing={isEditing}
          />
        );

      case 'elasticsearchApi':
        return (
          <ElasticsearchCredentialForm
            formData={formData.elasticsearchData || {}}
            onChange={(updates) =>
              onChange({ elasticsearchData: { ...formData.elasticsearchData, ...updates } })
            }
            isEditing={isEditing}
          />
        );

      case 'azure_blob_storage':
        return (
          <AzureBlobStorageCredentialForm
            value={formData.azureBlobStorageData || {}}
            onChange={(data) => onChange({ azureBlobStorageData: data })}
          />
        );

      case 'accessDocument':
        return (
          <AccessDocumentCredentialForm
            formData={{
              instanceId: formData.accessDocumentData?.instanceId || '',
              sharedSecret: formData.accessDocumentData?.sharedSecret || '',
              webServiceUrl: formData.accessDocumentData?.webServiceUrl || '',
            }}
            onChange={(updates) =>
              onChange({ accessDocumentData: { ...formData.accessDocumentData, ...updates } })
            }
            isEditing={isEditing}
          />
        );

      case 'donorfy':
        return (
          <DonorfyCredentialForm
            formData={{
              tenantId: formData.donorfyData?.tenantId || '',
              username: formData.donorfyData?.username || '',
              password: formData.donorfyData?.password || '',
            }}
            onChange={(updates) =>
              onChange({ donorfyData: { ...formData.donorfyData, ...updates } })
            }
            isEditing={isEditing}
          />
        );

      case 'peoplehr':
        return (
          <PeopleHRCredentialForm
            formData={{
              apiKey: formData.peopleHRData?.apiKey || '',
            }}
            onChange={(updates) =>
              onChange({ peopleHRData: { ...formData.peopleHRData, ...updates } })
            }
            isEditing={isEditing}
          />
        );

      case 'paycircle':
        return (
          <PayCircleCredentialForm
            formData={{
              username: formData.paycircleData?.username || '',
              password: formData.paycircleData?.password || '',
              baseUrl: formData.paycircleData?.baseUrl || '',
            }}
            onChange={(updates) =>
              onChange({ paycircleData: { ...formData.paycircleData, ...updates } })
            }
            isEditing={isEditing}
          />
        );

      case 'access-charity-crm':
        return (
          <AccessCharityCrmCredentialForm
            formData={{
              apiKey: formData.accessCharityCrmData?.apiKey || '',
              bearerToken: formData.accessCharityCrmData?.bearerToken || '',
              baseUrl: formData.accessCharityCrmData?.baseUrl || '',
            }}
            onChange={(updates) =>
              onChange({ accessCharityCrmData: { ...formData.accessCharityCrmData, ...updates } })
            }
            isEditing={isEditing}
          />
        );

      case 'access-agentic-platform':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="accessAgenticUrl">Base URL *</Label>
              <Input
                id="accessAgenticUrl"
                placeholder="https://peoplexd-agenticrostermiddleware-qa.azurewebsites.net"
                value={formData.accessAgenticPlatformData?.url || ''}
                onChange={(e) =>
                  onChange({
                    accessAgenticPlatformData: {
                      ...formData.accessAgenticPlatformData,
                      url: e.target.value,
                    },
                  })
                }
              />
              <p className="text-xs text-muted-foreground">
                The base URL of your Access Agentic Platform environment
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="accessAgenticApiKey">API Key *</Label>
              <Input
                id="accessAgenticApiKey"
                type="password"
                placeholder="Enter your API key"
                value={formData.accessAgenticPlatformData?.apiKey || ''}
                onChange={(e) =>
                  onChange({
                    accessAgenticPlatformData: {
                      ...formData.accessAgenticPlatformData,
                      apiKey: e.target.value,
                    },
                  })
                }
              />
              <p className="text-xs text-muted-foreground">
                The API key is sent in the X-API-Key header for authentication
              </p>
            </div>
          </div>
        );

      // Note: microsoft_teams_oauth and microsoft_outlook_oauth are handled
      // directly in CredentialDialog with flattened Azure AD / Connection tabs

      case 'turso':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="url">Database URL *</Label>
              <Input
                id="url"
                placeholder="libsql://your-database.turso.io"
                value={formData.url || ''}
                onChange={(e) => onChange({ url: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="authToken">Auth Token *</Label>
              <Input
                id="authToken"
                type="password"
                placeholder="Enter Turso auth token"
                value={formData.authToken || ''}
                onChange={(e) => onChange({ authToken: e.target.value })}
              />
            </div>
          </div>
        );

      case 'databricks':
        console.log('[CredentialDetailsTab] Rendering databricks form, formData:', {
          workspaceUrl: formData.workspaceUrl,
          databricksToken: formData.databricksToken ? '***' : 'empty',
          warehouseId: formData.warehouseId,
        });
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="workspaceUrl">Workspace URL *</Label>
              <Input
                id="workspaceUrl"
                placeholder="https://adb-xxx.xx.azuredatabricks.net"
                value={formData.workspaceUrl || ''}
                onChange={(e) => {
                  console.log('[CredentialDetailsTab] workspaceUrl changed:', e.target.value);
                  onChange({ workspaceUrl: e.target.value });
                }}
              />
              <p className="text-xs text-muted-foreground">
                Your Databricks workspace URL (e.g., https://adb-123456789.11.azuredatabricks.net)
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="databricksToken">Personal Access Token *</Label>
              <Input
                id="databricksToken"
                type="password"
                placeholder="dapi..."
                value={formData.databricksToken || ''}
                onChange={(e) => {
                  console.log('[CredentialDetailsTab] databricksToken changed');
                  onChange({ databricksToken: e.target.value });
                }}
              />
              <p className="text-xs text-muted-foreground">
                Generate a token from User Settings &gt; Developer &gt; Access Tokens
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="warehouseId">SQL Warehouse ID (optional)</Label>
              <Input
                id="warehouseId"
                placeholder="5c742f61f2594956"
                value={formData.warehouseId || ''}
                onChange={(e) => onChange({ warehouseId: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Default warehouse ID for SQL queries. Can be overridden per node.
              </p>
            </div>
          </div>
        );

      case 'aws':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="accessKeyId">Access Key ID *</Label>
              <Input
                id="accessKeyId"
                placeholder="AKIA..."
                value={formData.accessKeyId || ''}
                onChange={(e) => onChange({ accessKeyId: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="secretAccessKey">Secret Access Key *</Label>
              <Input
                id="secretAccessKey"
                type="password"
                placeholder="Enter secret access key"
                value={formData.secretAccessKey || ''}
                onChange={(e) => onChange({ secretAccessKey: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="region">Region</Label>
              <Input
                id="region"
                placeholder="us-east-1"
                value={formData.region || ''}
                onChange={(e) => onChange({ region: e.target.value })}
              />
            </div>
          </div>
        );

      case 'gcp':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="projectId">Project ID *</Label>
              <Input
                id="projectId"
                placeholder="my-gcp-project"
                value={formData.projectId || ''}
                onChange={(e) => onChange({ projectId: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="value">Service Account Key (JSON) *</Label>
              <Input
                id="value"
                type="password"
                placeholder="Paste service account key JSON"
                value={formData.value || ''}
                onChange={(e) => onChange({ value: e.target.value })}
              />
            </div>
          </div>
        );

      case 'smtp':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="host">SMTP Host *</Label>
                <Input
                  id="host"
                  placeholder="smtp.example.com"
                  value={formData.host || ''}
                  onChange={(e) => onChange({ host: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="port">Port</Label>
                <Input
                  id="port"
                  placeholder="587"
                  value={formData.port || ''}
                  onChange={(e) => onChange({ port: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  placeholder="username"
                  value={formData.username || ''}
                  onChange={(e) => onChange({ username: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="password"
                  value={formData.password || ''}
                  onChange={(e) => onChange({ password: e.target.value })}
                />
              </div>
            </div>
          </div>
        );

      default:
        // Generic fallback for other types
        return (
          <div className="space-y-2">
            <Label htmlFor="value">Value *</Label>
            <Input
              id="value"
              type="password"
              placeholder="Enter credential value"
              value={formData.value || ''}
              onChange={(e) => onChange({ value: e.target.value })}
            />
          </div>
        );
    }
  };

  return (
    <div className="space-y-4">
      {/* Type-Specific Fields */}
      {renderTypeFields()}
    </div>
  );
};

export default CredentialDetailsTab;
